import { FC, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useRelativeTime } from '@factorialco/deprecated-design-system'
import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { EyeVisible, Upsell } from '@factorialco/f0-react/icons/app'
import { AvailableModules } from 'generated/featureDefinitions'
import {
  PerformanceEvaluationAnswerHeaderActionsDocument,
  PerformanceEvaluationAnswerHeaderFragment,
} from 'generated/resources/graphql'
import { compact } from 'lodash'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { useLayoutContext } from 'modules/layout/hooks/useFullscreen'
import { ContextSidepanel } from 'modules/performance_process/components/review/answer/ContextSidepanel'
import { getEvaluationType } from 'modules/performance_process/components/review/answer/helpers/getEvaluationType'
import { statusColor, tagLabel } from 'modules/performance_process/lib/answerTag'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellingKit } from 'modules/upselling_kit/components'

type Props = {
  evaluation: PerformanceEvaluationAnswerHeaderFragment
}

export const EvaluationAnswerHeader: FC<Props> = ({ evaluation }) => {
  const { isEmbedded } = useLayoutContext()
  const location = useLocation()
  const navigate = useNavigate()

  const [showContextSidepanel, setShowContextSidepanel] = useState(false)
  const [showTrainingsUpsellModal, setShowTrainingsUpsellModal] = useState(false)

  const status = evaluation.status
  const type = evaluation.evaluationType
  const { title: evaluationTypeTitle, description } = type
    ? getEvaluationType(type)
    : { title: '', description: '' }
  const reviewIsFinished = evaluation.performanceReviewProcess?.status === 'finished'

  const targetAccessId = evaluation?.targetAccess?.id
  const deadline = evaluation.performanceReviewProcess?.endsAt

  const { formatted: formattedDeadline } = useRelativeTime({ date: deadline })

  const { company, canRequestUpsell } = useCurrentSession()

  const { data, loading } = useQuery(PerformanceEvaluationAnswerHeaderActionsDocument, {
    variables: { companyId: company.id },
  })

  // Hide the header when embedded in mobile WebView
  if (isEmbedded) return null

  const feedbacksFeatureEnabled =
    data?.features.featuresConnection.nodes.find((node) => node.name === 'dev_performance_feedback')
      ?.enabled ?? false

  const canRepublish = evaluation.answeredQuestionnaires?._policies?.republish?.can ?? false

  const isPublished = evaluation.status === 'published'

  const isRepublishModeFromNavigation = location.state?.isRepublishing === true

  const isRepublishing = canRepublish && isPublished && isRepublishModeFromNavigation

  const canUpsellTrainings =
    !loading && !data?.companies.trainingsModuleAvailability?.available && canRequestUpsell

  const targetEmployeeId = evaluation.targetAccess?.employeeName?.id
  const canSeeContextSidepanel = Boolean(evaluation.performanceReviewProcess && targetEmployeeId)

  const targetFullName = evaluation.targetAccess?.fullName
  const title = targetFullName ? `${targetFullName} - ${evaluationTypeTitle}` : evaluationTypeTitle

  const openContextSidepanel = () => setShowContextSidepanel(true)
  const closeContextSidepanel = () => setShowContextSidepanel(false)

  return (
    <>
      <ResourceHeader
        title={title}
        description={description}
        status={{
          label: '',
          text: tagLabel(status, isRepublishing),
          variant: statusColor(status, isRepublishing),
        }}
        primaryAction={
          canRepublish && !isRepublishing
            ? {
                label: i18n.t('performance_process.reviews.evaluations.edit'),
                onClick: () => navigate(location.pathname, { state: { isRepublishing: true } }),
              }
            : undefined
        }
        secondaryActions={[
          {
            label: i18n.t('performance_process.reviews.answer_evaluation.context_sidepanel.button'),
            icon: EyeVisible,
            onClick: openContextSidepanel,
            variant: 'outline',
            isVisible: canSeeContextSidepanel,
          },
          {
            label: i18n.t('performance_process.targets.see_past_courses.button'),
            icon: Upsell,
            onClick: () => setShowTrainingsUpsellModal(true),
            variant: 'outlinePromote',
            isVisible: canUpsellTrainings,
          },
        ]}
        metadata={compact([
          !!(!reviewIsFinished && deadline) && {
            label: i18n.t('performance_process.reviews.common.due_date'),
            value: {
              type: 'text',
              content: formattedDeadline || dateBackendDateTimeToUI(deadline),
            },
          },
          !!(evaluation.reviewerAccess && type !== 'self') && {
            label: i18n.t('performance_process.reviews.answer_evaluation.header.evaluator'),
            value: {
              type: 'avatar',
              text: evaluation.reviewerAccess.fullName,
              variant: {
                firstName: evaluation.reviewerAccess.firstName,
                lastName: evaluation.reviewerAccess.lastName,
                src: buildUrl(evaluation.reviewerAccess.avatar),
                type: 'person',
              },
            },
          },
          !!evaluation.targetAccess && {
            label: i18n.t('performance_process.reviews.answer_evaluation.header.evaluated'),
            value: {
              type: 'avatar',
              text: evaluation.targetAccess.fullName,
              variant: {
                firstName: evaluation.targetAccess.firstName,
                lastName: evaluation.targetAccess.lastName,
                src: buildUrl(evaluation.targetAccess.avatar),
                type: 'person',
              },
            },
          },
        ])}
      />
      {showContextSidepanel &&
        evaluation.performanceReviewProcess &&
        targetEmployeeId &&
        targetAccessId && (
          <ContextSidepanel
            open
            onClose={closeContextSidepanel}
            employeeId={targetEmployeeId}
            targetAccessId={targetAccessId}
            reviewProcessStartedAt={evaluation.performanceReviewProcess.startsAt}
            isPerformanceFeedbackEnabled={feedbacksFeatureEnabled}
            canUpsellTrainings={canUpsellTrainings}
          />
        )}
      {showTrainingsUpsellModal && (
        <UpsellingKit.ProductModal
          isOpen
          onClose={() => setShowTrainingsUpsellModal(false)}
          requestSource="Performance - view past courses"
          moduleRequested={AvailableModules.trainings}
          requestReason={ValidReasons.MissingAddon}
          pageSource="performance_process"
        />
      )}
    </>
  )
}
